import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@ui/Button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "@ui/Select";
import { DEFAULT_FETCH_LIMIT } from "@/constants/fetchLimits";
import type { Pagination } from "@/types/Pagination";
import { getNextPageParam } from "@/utils/paginated";
import { cn } from "@/utils/tailwind";

interface PaginatedSelectProps<T> {
  fetchFn: (limit: number, offset: number) => Promise<Pagination<T>>;
  getOptionLabel: (item: T) => string;
  getOptionValue: (item: T) => string;
  value?: string;
  onChange?: (value: string) => void;
  onSelectItem?: (item: T) => void;
  limit?: number;
  triggerClassName?: string;
  disabled?: boolean;
  isError: boolean;
  queryKey: unknown[];
  dataTest?: string;
}

export function PaginatedSelect<T>({
  fetchFn,
  getOptionLabel,
  getOptionValue,
  value,
  onChange,
  onSelectItem,
  limit = DEFAULT_FETCH_LIMIT,
  triggerClassName,
  disabled,
  isError,
  dataTest = "",
  queryKey,
}: PaginatedSelectProps<T>) {
  const { t } = useTranslation();
  const { ref, entry } = useInView({ trackVisibility: true, delay: 100 });

  const {
    data,
    fetchNextPage,
    isPending,
    isError: isQueryError,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 0 }) => fetchFn(limit, pageParam),
    getNextPageParam: getNextPageParam(limit),
    initialPageParam: 0,
    refetchOnMount: false,
    enabled: !disabled,
  });

  const items = data?.pages.flatMap(page => page.results) ?? [];

  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isPending) {
      void fetchNextPage();
    }
  }, [entry?.isIntersecting, fetchNextPage, isPending, hasNextPage]);

  const handleValueChange = (selectedValue: string) => {
    onChange?.(selectedValue);

    const selectedItem = items.find(item => getOptionValue(item) === selectedValue);
    if (selectedItem) onSelectItem?.(selectedItem);
  };

  return (
    <Select value={value} onValueChange={handleValueChange}>
      <SelectTrigger
        className={cn(
          "w-full sm:w-[273px]",
          isError && "border-destructive",
          disabled && "bg-card pointer-events-none",
          triggerClassName,
        )}
        disabled={disabled}
        data-test={dataTest}
      >
        <SelectValue />
      </SelectTrigger>

      <SelectContent
        className="max-h-[200px] overflow-y-auto"
        onCloseAutoFocus={e => e.preventDefault()}
      >
        <SelectGroup>
          {isPending ? (
            <SelectLoader />
          ) : isQueryError ? (
            <div className="text-destructive mx-auto max-w-60 space-y-4 py-6 text-center text-xs">
              <p data-test={`${dataTest}-error`}>{t("failedToLoadData")}</p>
              <Button
                variant="secondary"
                className="text-foreground h-5 px-3 text-xs uppercase"
                data-test={`${dataTest}-retry`}
              >
                {t("refresh")}
              </Button>
            </div>
          ) : items.length === 0 ? (
            <NoSelectOptionsAvailable />
          ) : (
            items.map(item => (
              <SelectItem
                key={getOptionValue(item)}
                value={getOptionValue(item)}
                data-test={`${dataTest}-option-${getOptionValue(item)}`}
              >
                {getOptionLabel(item)}
              </SelectItem>
            ))
          )}

          {isFetchingNextPage && <SelectLoader />}

          {hasNextPage && <div ref={ref} data-test="scroller-trigger" className="mb-1 h-1" />}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

export const NoSelectOptionsAvailable = () => {
  const { t } = useTranslation();

  return (
    <div className="text-muted-foreground px-2 py-6 text-center text-xs">
      {t("noOptionsAvailable")}
    </div>
  );
};

export const SelectLoader = () => {
  return (
    <div className="flex justify-center py-6">
      <Loader2 className="size-4 animate-spin" />
    </div>
  );
};
